'use client';

import { memo, useEffect, useState } from 'react';

interface AvatarProps {
    profilePictureUrl?: string | null;
    displayName?: string | null;
    givenName?: string | null;
    surname?: string | null;
    size?: 'sm' | 'md' | 'lg';
    onClick?: () => void;
    className?: string;
}

const SIZE_CLASSES = {
    sm: 'w-8 h-8 text-xs',
    md: 'w-10 h-10 text-sm',
    lg: 'w-16 h-16 text-xl',
};

function getInitials(displayName?: string | null, givenName?: string | null, surname?: string | null): string {
    if (givenName && surname) {
        return (givenName[0] + surname[0]).toUpperCase();
    }
    if (givenName) {
        return givenName.substring(0, 2).toUpperCase();
    }
    if (displayName) {
        const words = displayName.trim().split(/\s+/).filter(Boolean);
        if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
        if (words.length === 1) return words[0].substring(0, 2).toUpperCase();
    }
    return 'U';
}

const Avatar = memo(function Avatar({
    profilePictureUrl,
    displayName,
    givenName,
    surname,
    size = 'md',
    onClick,
    className = '',
}: AvatarProps) {
    const [imgError, setImgError] = useState(false);
    useEffect(() => { setImgError(false); }, [profilePictureUrl]);
    const sizeClass = SIZE_CLASSES[size];
    const initials = getInitials(displayName, givenName, surname);
    const cursorClass = onClick ? 'cursor-pointer' : '';

    if (profilePictureUrl && !imgError) {
        return (
            <img
                src={profilePictureUrl}
                alt={displayName ?? 'Avatar'}
                onError={() => setImgError(true)}
                onClick={onClick}
                className={`${sizeClass} rounded-full object-cover select-none ${cursorClass} ${className}`}
            />
        );
    }

    return (
        <div
            onClick={onClick}
            className={`${sizeClass} rounded-full bg-primary text-primary-foreground flex items-center justify-center font-semibold select-none ${cursorClass} ${className}`}
        >
            {initials}
        </div>
    );
});

Avatar.displayName = 'Avatar';

export default Avatar;
